import React, { useState } from 'react'
import { X, Gift, ArrowRight, CheckCircle2, Info } from 'lucide-react'
import { lotesService } from '../../services/lotesService'
import toast from 'react-hot-toast'

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

/**
 * Leilão beneficente: clona um lote FATURADO para ser vendido novamente.
 * Nenhum animal é retirado — o novo lote entra com a quantidade inteira e os
 * dados da venda anterior já preenchidos (basta trocar o comprador e faturar).
 */
const DoarNovamenteModal = ({ isOpen, onClose, onSuccess, organizadorId, lote }) => {
  const [loading, setLoading] = useState(false)

  if (!isOpen || !lote) return null

  // Preview do número (o backend define o sufixo real: A, B, C…)
  const m = lote.numero_lote?.match(/^(\d+)/)
  const baseNum = m ? m[1] : lote.numero_lote
  const novoNumero = `${baseNum}A`

  const valorUni = lote.valor_venda_unidade ? parseFloat(lote.valor_venda_unidade) : null
  const totalEstimado = lote.valor_venda_total
    ? parseFloat(lote.valor_venda_total)
    : (valorUni && lote.qtd ? valorUni * lote.qtd : null)

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const resultado = await lotesService.doarNovamenteLote(organizadorId, lote.id)
      const { lote_novo } = resultado
      toast.success(
        `Lote ${lote_novo.numero_lote} criado com ${lote_novo.qtd} ${lote_novo.qtd === 1 ? 'animal' : 'animais'}. Troque o comprador e fature.`,
        { duration: 5000 }
      )
      onSuccess(resultado)
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao doar novamente'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-pink-100 flex items-center justify-center">
              <Gift className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Doar Novamente</h2>
              <p className="text-xs text-gray-500">
                Lote {lote.numero_lote} · {lote.qtd} {lote.qtd === 1 ? 'animal' : 'animais'}
                <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-semibold">FATURADO</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          <div className="flex gap-3 p-3.5 bg-pink-50 border border-pink-200 rounded-xl">
            <Info className="w-4 h-4 text-pink-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-pink-800 leading-relaxed">
              O comprador doou os animais de volta ao leilão. Vamos gerar um <strong>novo lote</strong> com
              os mesmos animais (nenhum é retirado) e com os dados da venda anterior já preenchidos.
              O lote original <strong>não é alterado</strong>.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">O que vai acontecer</p>
            <div className="flex items-center gap-3">
              {/* Lote original */}
              <div className="flex-1 border border-gray-200 rounded-xl p-3.5 bg-gray-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-500">Lote original</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">FATURADO</span>
                </div>
                <p className="text-lg font-bold text-gray-900">{lote.numero_lote}</p>
                <p className="text-sm text-gray-600">{lote.qtd} animais · intacto</p>
              </div>

              <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />

              {/* Novo lote */}
              <div className="flex-1 border border-pink-200 rounded-xl p-3.5 bg-pink-50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-pink-600">Novo lote</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">NOVO</span>
                </div>
                <p className="text-lg font-bold text-pink-700">{novoNumero}*</p>
                <p className="text-sm font-semibold text-pink-800">{lote.qtd} animais</p>
                {totalEstimado !== null && (
                  <p className="text-xs text-pink-600 mt-1">≈ {formatCurrency(totalEstimado)}</p>
                )}
              </div>
            </div>
            <p className="text-[11px] text-gray-400 flex items-center gap-1">
              <Info className="w-3 h-3" />
              *O número real será o próximo sufixo disponível (A, B, C…). Abriremos o novo lote para você trocar o comprador e faturar.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2 text-sm font-semibold bg-pink-600 hover:bg-pink-700 text-white rounded-lg
              transition-colors disabled:opacity-50 flex items-center gap-2 min-w-[150px] justify-center">
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Processando…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Gerar novo lote
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DoarNovamenteModal
